import { useInfiniteQuery, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import qs from 'qs'
import { useAccountMeQuery } from '~/api/accounts'
import { OffsetLimitPaginationResponse } from '~/api/common'
import { GradeFeedbackDirection } from '~/api/csa/csa'
import { HireType, JOB_QUERY, JobFlowStatus, ModuleType, PayType, VendorPayment } from '~/api/jobs'
import { ShiftDifferential } from '~/api/shifts'
import { MeetingType } from '~/api/vendors'
import {
  ExpenseTypeEnum,
  SourceTypeEnum,
  workOrderApi,
  WorkOrderChangeSet,
  WorkTypeRecord,
  WorkOrderEffectiveDatedFields
} from '~/api/work_order'
import { ChargeCodeRawValues } from '~/charge_codes/types'
import { AddressData } from '~/components/v2/Address/Address'
import { formatDataDate } from '~/common/dateUtils'
import fetch from '~/common/fetch'
import { FetchOptions } from '~/common/fetchHelpers'
import { floatToPercent } from '~/common/numberUtils'
import { snakeCaseKeysDeep } from '~/common/objectUtils'
import { queryClient } from '~/common/react-query/queryClient'
import { queryOptions } from '~/common/react-query/queryOptions'
import { DateString, DecimalString, Integer, Money, PrimaryKey, TimeString, TimestampString } from '~/common/types'
import { GenericFields } from '~/invoice/types'
import { VendorStatus } from '~/vendor_profile/constants'
import { ProgramRateType } from '~/calculation_engine/constants'

const JOB_APPLICATION = 'job_application'
const WORK_TYPES_QUERY_KEY = 'work-types'

type JobApplicationConfigParams = {
  bu_id?: number
  // can_resubmit_rejected_candidates
  //   returns candidate submission settings at global level
  //   it requires job_id param to read any job level overrides
  job_id?: number
  job_application_id?: number
}

export const jobApplicationKeys = {
  all: ['job-applications'] as const,
  lists: () => [...jobApplicationKeys.all, 'list'] as const,
  list: (filters?: Record<string, any>) => [...jobApplicationKeys.lists(), { filters }] as const,
  details: () => [...jobApplicationKeys.all, 'detail'] as const,
  detail: (id?: number) => [...jobApplicationKeys.details(), id] as const,
  config: (filters?: JobApplicationConfigParams) =>
    filters
      ? ([...jobApplicationKeys.all, 'config', { filters }] as const)
      : ([...jobApplicationKeys.all, 'config'] as const)
}

// Split-list rows and the detail card are cached under `applicants`, separate from jobApplicationKeys.
const APPLICANTS_QUERY_KEY = ['applicants'] as const

export const invalidateJobApplicationQueries = () =>
  Promise.all([
    queryClient.invalidateQueries(jobApplicationKeys.all),
    queryClient.invalidateQueries(APPLICANTS_QUERY_KEY)
  ])

export const interviewKeys = {
  all: ['interviews'] as const,
  lists: () => [...interviewKeys.all, 'list'] as const,
  list: (filters?: Record<string, any>) => [...interviewKeys.lists(), { filters }] as const,
  details: () => [...interviewKeys.all, 'detail'] as const,
  detail: (id?: number) => [...interviewKeys.details(), id] as const
}

export const offerReleaseConfigKeys = {
  all: ['offer-release-config'] as const,
  details: () => [...offerReleaseConfigKeys.all, 'detail'] as const,
  detail: (ids?: OfferConfigParams) => [...offerReleaseConfigKeys.details(), { ids }] as const
}

export const office365MeetingSuggestionKeys = {
  all: ['office365-meeting-suggestions'] as const,
  lists: () => [...office365MeetingSuggestionKeys.all, 'list'] as const,
  list: (filters?: Record<string, any>) => [...office365MeetingSuggestionKeys.lists(), { filters }] as const
}

export const jobApplicationsApi = {
  offerForm: (jobAppId: number, fetchOpts?: FetchOptions): Promise<JobOfferFormData> =>
    fetch.get(`/api/v2/job_applications/${jobAppId}/offer-form`, fetchOpts),

  applicantDetails: (jobAppId: number, fetchOpts?: FetchOptions): Promise<ApplicantDetails> =>
    fetch.get(`/api/v2/job_applications/${jobAppId}/applicant-details/`, fetchOpts),

  workTypes: (jobAppId: number, workTypeProfileId: number): Promise<WorkTypeRecord[]> =>
    fetch.get(`/api/v2/job_applications/${jobAppId}/work-types?work_type_profile=${workTypeProfileId}`),

  withdraw: (data: RejectWithdrawJobApplicationParams) =>
    fetch.post(`/api/v2/job_applications/${data.job_app_id}/withdraw`, data),

  reject: (data: RejectWithdrawJobApplicationParams) =>
    fetch.put(`/api/job_application_reject/${data.job_app_id}`, data),

  bulk_reject: (data: BulkRejectJobApplicationParams) => fetch.post(`/api/v2/job_applications/bulk_reject`, data),

  scheduleMeeting: (data: JobAppScheduleMeetingParams) =>
    fetch.post(`/api/v2/job_application/schedule_meeting/${data.jobAppId}/`, data),

  rescheduleMeeting: (data: JobAppRescheduleMeetingParams) =>
    fetch.post(`/api/v2/job_application/reschedule_meeting/${data.eventId}/`, data),

  requestRescheduleMeeting: (data: JobAppRequestRescheduleMeetingParams): Promise<void> =>
    fetch.post(`/job_applications/request_reschedule/${data.eventId}/`, data),

  cancelMeeting: (data: JobAppCancelMeetingParams) =>
    fetch.post(`/job_applications/respond_interview/${data.eventId}/`, data),

  checklistRejectionReasons: () => fetch.get(`/api/v2/customization/datasources/checklist_rejection_reason/rows/`),

  jobApplicationDetails: (id: number, fetchOpts?: FetchOptions): Promise<JobApplication> =>
    fetch.get(`/api/v2/job_applications/${id}`, fetchOpts),

  interviewEventDetails: (id: number): Promise<InterviewEvent> => fetch.get(`/schedule/api_get_schedule_event/${id}`),

  listJobApplications: (queryParams: Record<string, any>): Promise<JobApplicationResponse> =>
    fetch.get(`/api/v2/job_applications/?${qs.stringify(queryParams)}`),

  getReapplyCandidateByJobApplication: ({
    jobApplicationId,
    candidateId
  }: ApplicableCandidateByJobApplicationInput): Promise<ApplicableCandidate> =>
    fetch.get(`/api/applicable-candidate-by-job-application/${jobApplicationId}/candidate/${candidateId}?`),

  getApplicableCandidatesByVendor,

  config: (queryParams: Record<string, any>): Promise<JobApplicationConfig> =>
    fetch.get(`/api/v2/job_application/config/?${qs.stringify(queryParams)}`),

  office365MeetingSuggestions: (date: string, timezone?: string): Promise<Office365MeetingSuggestions[]> => {
    const tz = timezone ? `&timezone=${timezone}` : ''
    return fetch.get(`/api/v2/office365/calendar/meeting_suggestions?date=${date}${tz}`)
  },

  // config returned by offer_release/config is common to offer release/revise/onboard
  // TODO: should we modify offer_release/config url to offer/config url?
  offerConfig: (ids?: OfferConfigParams, fetchOpts?: FetchOptions): Promise<OfferConfig> =>
    fetch.get(
      `/api/v2/offer_release/config/?${qs.stringify({
        job_app_id: ids?.jobAppId,
        job_id: ids?.jobId,
        is_onboarding: ids?.is_onboarding
      })}`,
      fetchOpts
    ),

  getBillRate: (id: number, queryParams: ContractorBillRateParams): Promise<ContractorBillRateResponse> =>
    fetch.get(`/api/job_application/get_bill_rate/${id}/?${qs.stringify(queryParams)}`),

  acceptOffer: (id: number) => fetch.post(`/api/v2/accept_offer/${id}`),

  undoReject: (workOrderId: number) => fetch.post(`/api/v2/work-orders/${workOrderId}/undo-reject`)
}

function getApplicableCandidatesByVendor({
  vendorId,
  job,
  filter = '',
  limit = 10,
  offset = 0
}: ApplicableCandidatesInput): Promise<OffsetLimitPaginationResponse<ApplicableCandidate>> {
  const {
    jobId,
    payTypeId: payType,
    businessUnitId,
    locationId,
    exemptStatus,
    startDate,
    endDate,
    jobCategoryId,
    jobTemplateId,
    jobTemplateJobTypeId,
    isPreIdentified,
    hireType,
    currencyCode,
    useRateCard,
    calculationPack,
    programRateType,
    programTeamEntityId
  } = job

  const query = encodeURIComponent(filter)
  const prefix = `/api/applicable-candidates-by-vendor/${vendorId}/?limit=${limit}&offset=${offset}&query=${query}`

  if (jobId) {
    return fetch.get(`${prefix}&job_id=${jobId}&is_pre_identified=${isPreIdentified}`)
  }

  return fetch.get(
    `${prefix}&${qs.stringify(
      snakeCaseKeysDeep({
        payType,
        businessUnitId,
        locationId,
        exemptStatus,
        startDate,
        endDate,
        jobCategoryId,
        jobTemplateId,
        jobTemplateJobTypeId,
        isPreIdentified,
        hireType,
        currencyCode,
        useRateCard,
        calculationPack,
        programRateType,
        programTeamEntityId
      })
    )}`
  )
}

// OfferStatus is the "offer_status" of application - it IS derived from "current_status" but splitting them was beyond
// the scope of this component - See JobApplication Model
export enum OfferStatus {
  Applied = 1,
  Selected = 2,
  Withdrawn = 5,
  OfferAccepted = 6,
  OfferDeclined = 7,
  Onboarded = 8,
  ApprovalPending = 10,
  OnboardedForFutureDate = 11,
  ApprovalRejected = 12
}

export interface RemoveDuplicateFlagParams {
  applicationId: number
}

export const useRemoveDuplicateFlagMutation = (opts?: UseMutationOptions<RemoveDuplicateFlagParams, unknown, any>) => {
  return useMutation({
    mutationFn: async (data: RemoveDuplicateFlagParams) => {
      return fetch.post(`/api/v2/set_not_duplicate_flag/${data.applicationId}`, {
        is_not_duplicate: true
      })
    },
    ...opts,
    onSuccess(...args) {
      if (opts?.onSuccess !== undefined) {
        opts.onSuccess(...args)
      }
      invalidateJobApplicationQueries()
    }
  })
}

export interface JobApplicationConfig {
  phone_number_required: boolean
  video_link_required: boolean
  office365_meeting_suggestions_enabled: boolean
  can_message: boolean
  default_manager_filter_to_shortlisted: boolean
  is_bmi_enabled: boolean
  can_resubmit_rejected_candidates: boolean
  can_bulk_onboard: boolean
  can_bulk_reject: boolean
  can_view_bulk_action_result: boolean
  can_edit_interview: boolean
  can_cancel_interview: boolean
  show_hired_score_grade: boolean
  show_contingent_sourcing_agent_grade: boolean
  show_csa_grade_feedback: boolean
  can_view_contractor_bill_rate: boolean
  can_view_vendor_company: boolean
  can_view_shortlisted_only: boolean
  is_bulk_onboard_update_all_enabled: boolean
  can_read_feedback_rating: boolean
  can_write_feedback_rating: boolean
  can_read_feedback_notes: boolean
  can_write_feedback_notes: boolean
}

/**
 * This is the type that comes back directly from the applicable candidate API endpoints.  It's extended in the UI at will.
 *
 * @remarks
 * The extra attributes that are potentially added to it are held in the `ApplicableCandidate` type.
 * */
export interface ApplicableCandidateAPIResponse {
  already_hired: boolean
  currency_code: string
  do_not_hire?: boolean
  eligible: boolean
  email?: string
  experience?: number
  full_name: string
  has_resume: boolean
  id: number
  ineligible_message: string | null
  initials: string
  is_direct_hire_job: boolean
  is_erased: boolean
  is_scheduled_payments_job: boolean
  markup_override?: string
  pay_rate_override?: string
  period_bill_rate: string
  period_markup: string
  period_pay_rate: string
  preferred_jobs: []
  profile_pic: string
  vendor_id: number
  vendor_name: string
}

export interface ApplicableCandidate extends Omit<ApplicableCandidateAPIResponse, 'period_bill_rate'> {
  bill_rate: number
  pay_rate: number
  location?: number
  start_date?: Date | null
  end_date?: Date | null
  period_bill_rate?: string
  calculation_pack?: number
}

// Todo (Tim) I hate this model...
export interface JobLike {
  jobId: number
  payTypeId?: PayType
  businessUnitId?: number
  locationId?: number
  exemptStatus?: string
  startDate?: string
  endDate?: string
  jobCategoryId?: number
  jobTemplateId?: number
  jobTemplateJobTypeId?: number
  isPreIdentified?: boolean
  currencyCode?: string
  isVendor?: boolean
  hireType?: HireType
  useRateCard?: boolean
  calculationPack?: number
  programRateType?: string
  programTeamEntityId?: number
}

interface ApplicableCandidatesInput {
  vendorId: number
  job: JobLike
  filter?: string
  limit?: number
  offset?: number
  candidateId?: number
}

interface ApplicableCandidateByJobApplicationInput {
  jobApplicationId: number
  candidateId: number
}

export const useChecklistRejectionReasonsQuery = () =>
  useQuery({
    queryKey: ['job/rejection_reasons'],
    queryFn: (): Promise<OffsetLimitPaginationResponse<{ id: number; display_name: string; is_active: boolean }>> =>
      jobApplicationsApi.checklistRejectionReasons()
  })

// JobApplicationStatus is the "current_status" of an application
export enum JobApplicationStatus {
  VendorApplied = 1,
  ClientInterviewing = 2,
  ClientRejected = 3,
  PendingOfferRelease = 4,
  ClientOfferReleased = 5,
  VendorOfferDeclined = 6,
  VendorWithdrawn = 7,
  VendorVerificationInProcess = 8,
  ReadyToOnboard = 10,
  ContractedOnboarded = 11,
  JobClosed = 12,
  // OnboardedButNotActive = 13, Removed
  PendingOnboardApprovals = 14,
  PendingOfferAcceptance = 16,
  ApprovalRejected = 15
}

export const FASTPATH_STATUS_TOKEN = {
  [JobApplicationStatus.VendorVerificationInProcess]: 'job_application.status.vendor_verification_in_process',
  [JobApplicationStatus.ReadyToOnboard]: 'job_application.status.ready_to_onboard',
  [JobApplicationStatus.ContractedOnboarded]: 'job_application.status.contracted_onboarded'
}

export enum InterviewEventStatus {
  UNDECIDED = '1',
  ACCEPTED = '2',
  DECLINED = '3',
  CANCELLED = '4'
}

export enum InterviewEventModes {
  FACE_TO_FACE = '1',
  TELEPHONE = '2',
  VIDEO = '3'
}

export interface InterviewEvent {
  id: number
  status: InterviewEventStatus
  candidate_id: number
  job_id: number
  vendor_email?: string
  title: string
  timezone: string
  start: string
  end: string
  mod_interview: string
  interview_details: string
  conf_room: string
  bridge_pin: string
  isd_code: string
  description: string
  share_profile: string
  created_by_display: string
}

export interface WorkOrderHistoryDetails {
  active: number
  inactive: number
  ready_to_onboard: number
}

export interface JobApplication {
  id: number
  applicant: string // This is the candidate id... as a string :(
  job_applied: number // The ID for the job associated with the job application
  job_applied_title: string
  work_order_id: number
  readonly current_status: JobApplicationStatus // Read only field that represents the most recent status
  readonly process_state: JobFlowStatus
  applicant_name: string
  is_duplicate?: boolean
  is_shortlisted: boolean
  vendor_email?: string
  candidate_pay_rate?: number | null
  optimized_rate?: number | null
  rating?: number // Star ratings
  updated_at: Date
  created_at: Date
  start_date_no_tz: Date | string
  end_date: Date | string
  work_site?: number
  payment_details: PaymentDetails | null
  bmi?: number | null
  candidate_source_display?: string
  experience?: string
  candidate_generic_fields?: GenericFields
  work_order_generic_fields?: GenericFields
  work_type_profile?: number
  hire_type?: HireType
  contract_pay_type?: PayType
  resource_manager?: number
  business_unit?: number
  job_template_job_type?: number
  job_template?: number
  calendar_pay_profile?: number
  work_week_start_time?: TimeString
  overtime_profile?: number
  additional_managers?: number[]
  contractor_email?: string
  contractor_username?: string
  msp_contractor_id?: string
  client_contractor_id?: string
  external_id?: string
  timesheet_approvers?: number[]
  expense_approvers?: number[]
  expense_type?: ExpenseTypeEnum
  source_type?: SourceTypeEnum
  currency_code?: string
}

export interface JobApplicationListItem {
  id: number
  candidate_id: number
  applicant_name: string
  are_rates_valid?: {
    is_above_bill_rate: boolean
    is_below_bill_rate: boolean
    is_above_pay_rate: boolean
    is_below_pay_rate: boolean
  } | null
  bmi?: number | null
  hiredscore_grade?: string | null
  contingent_sourcing_agent_grade?: string | null
  contingent_sourcing_agent_rationale?: string | null
  contingent_sourcing_agent_match_description?: string | null
  is_csa_grades_opt_in?: boolean | null
  contingent_sourcing_agent_grade_feedback_direction?: GradeFeedbackDirection | null
  candidate_pay_rate?: number | Money | null
  offered_pay_rate?: number | null
  candidate_pay_rate_display?: string
  candidate_source_display?: string | null
  contract_to_hire?: string | null
  contractor_classification?: string | null
  created_at: TimestampString
  readonly current_status: JobApplicationStatus // Read only field that represents the most recent status
  direct_hire?: string | null
  end_date: DateString | null
  experience?: string | number | null
  readonly has_resume: boolean
  readonly hired_candidate_id?: number
  is_duplicate?: boolean
  is_on_do_not_rehire_list?: boolean
  readonly is_pending_bulk_update?: boolean
  is_shortlisted: boolean
  job_applied_title: string
  markup?: string | null
  offered_markup?: number
  offered_overtime_markup?: number
  offered_double_time_markup?: number
  bill_rate?: number | Money | null
  optimized_rate_display?: string
  readonly previous_experience_at_client?: string | null
  readonly process_state: JobFlowStatus
  profile_pic: string | null
  rating: number | null
  start_date_no_tz: DateString | null
  updated_at: TimestampString
  vendor_company_name?: string | null
  weekday_travel?: string | null
  readonly work_order_history_details: number
  work_order_id: number
  readonly permitted_actions?: Record<string, boolean>
}

// existing JobApplication interface is based on JobApplicationLegacySerializer (soon to be deprecated)
// this interface is based on WorkOrderOfferFormDataSerializer
// used for Job Offer Release, Revise & Onboard form data
export interface JobOfferFormData {
  // WorkOrderOfferFormDataSerializer (Offer Release)
  id: PrimaryKey
  additional_managers: PrimaryKey[]
  applicant: DecimalString
  applicant_full_name: string
  vendor_entity_name: string | null
  business_unit: PrimaryKey
  calculation_pack: PrimaryKey | null
  calendar_pay_profile: PrimaryKey | null
  candidate_generic_fields: GenericFields | null
  candidate_pay_rate: number
  contract_pay_type: PayType
  candidate_markup: number
  overtime_markup?: number | null
  double_time_markup?: number | null
  currency_code: string
  end_date: DateString
  end_time: TimeString | null
  end_timezone: string | null
  expense_type: ExpenseTypeEnum
  hire_type: HireType
  is_pay_rate_driven: boolean
  job_id: PrimaryKey
  job_title: string
  job_end_date: DateString
  job_start_date: DateString
  job_template: PrimaryKey
  job_template_job_type: PrimaryKey
  job_category: PrimaryKey
  job_markup: number
  job_calculation_pack: PrimaryKey | null
  sow_job_and_billable: boolean | null
  optimized_rate: number | null
  overtime_profile: PrimaryKey | null
  payment_details: PaymentDetails | null
  program_rate_type: ProgramRateType
  program_team_entity_id?: PrimaryKey | null
  resource_manager: PrimaryKey
  source_type: SourceTypeEnum
  start_date_no_tz: DateString
  vendor_id: number
  vendor_payment_term: VendorPayment | null
  vendor_valid_email_domains: string[]
  denied_email_domains: string[]
  work_order_first_offer_at: DateString | null
  work_order_generic_fields: GenericFields | null
  work_site: PrimaryKey
  work_type_profile: PrimaryKey | null
  work_week: Integer
  work_week_start_time: TimeString | null
  worker_classification: PrimaryKey | null
  workday_position: string | null
  apply_shifts?: boolean
  shifts?: PrimaryKey[]
  shift_differential_mapping?: ShiftDifferential[]
  shift_strategy?: number | null
  event_based_expenses?: PrimaryKey[]
  job_module?: number | null
  default_event_based_expense_cc_data?: ChargeCodeRawValues
  billing_mode?: number
  billing_schedule?: number
  can_edit_billing_mode: boolean
  effective_dated_fields: WorkOrderEffectiveDatedFields[]

  // WorkOrderOfferFormDataSerializer (Offer Revise, Onboard)
  client_contractor_id?: DecimalString
  auth_provider?: number | null
  is_authentication_method_read_only?: boolean
  contractor_email?: string
  contractor_username?: string
  external_id?: DecimalString
  expense_approvers?: PrimaryKey[]
  msp_contractor_id?: DecimalString
  timesheet_approvers?: PrimaryKey[]
  work_order_id?: PrimaryKey
  rate_schedule_type: 'guided_rates' | 'manual' | null
  use_hcm_workday_id_as_vndly_username?: boolean
  hcm_workday_id?: string | null
  has_existing_profile?: boolean
}

export interface PaymentDetails {
  fee_amount: number
  number_of_payments: number
}

export const useJobApplicationQuery = (id?: number, queryOptions?: UseQueryOptions<JobApplication>) => {
  const key = jobApplicationKeys.detail(id)
  return useQuery({
    queryKey: key,
    queryFn: () =>
      id ? jobApplicationsApi.jobApplicationDetails(id) : Promise.reject(new Error('Invalid Job Application Id')),
    enabled: !!id,
    ...queryOptions
  })
}

export const useInterviewEventQuery = (id?: number) => {
  const key = interviewKeys.detail(id)
  return useQuery({
    queryKey: key,
    queryFn: () => jobApplicationsApi.interviewEventDetails(id!),
    enabled: !!id
  })
}

export type ApplicableCandidateQueryOptions = UseQueryOptions<OffsetLimitPaginationResponse<ApplicableCandidate>[]>

/**
 * Only require { jobId, isPreIdentified } on the job object to make the call.
 */
export function useApplicableCandidatesByVendorInfiniteQuery(
  job: JobLike | undefined,
  { filter = '' } = {},
  vendorId = null
) {
  const { data: { vendor_entity_id } = {} } = useAccountMeQuery()

  const params = {
    job: job ?? undefined,
    vendorId: vendorId ?? vendor_entity_id ?? undefined,
    filter
  }
  return useInfiniteQuery({
    queryKey: ['job/applicable_candidates', params],
    queryFn: ({ pageParam = 0 }) => {
      // @ts-expect-error This function won't be called with undefined job or vendorId
      // because `enabled` ensures that job and vendorId are defined first
      return getApplicableCandidatesByVendor({ ...params, limit: 10, offset: pageParam })
    },
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false,
    enabled: params.job != null && params.vendorId != null
  })
}

export function useApplicableCandidateByJobApplication(
  candidateId: number | undefined,
  jobApplicationId: number | undefined
) {
  const params = {
    jobApplicationId,
    candidateId
  }
  return useQuery({
    queryKey: ['job/applicable_candidate', params],
    queryFn: () => {
      return jobApplicationsApi.getReapplyCandidateByJobApplication({
        jobApplicationId: jobApplicationId!,
        candidateId: candidateId!
      })
    },
    enabled: !!params.candidateId && !!params.jobApplicationId
  })
}

export interface JobApplicationOfferPayload {
  wo_data: OfferWorkOrderData
  contractor_data?: OfferContractorData
  connector_data?: { workday?: { position?: PrimaryKey } }
  approval_email?: Record<string, any>
  waive_verification?: boolean
}

export type OfferWorkOrderData = {
  // changes - primary field delivery mechanism (most fields now go through here)
  changes?: WorkOrderChangeSet[]

  // Special fields that need direct handling (not via changes)
  end_time?: TimeString | null
  end_timezone?: string | null
  generic_fields?: GenericFields | null
  currency_code?: string

  // Direct hire fields - backend derives values from these
  direct_hire_salary?: Money
  direct_hire_rate?: Money
  direct_hire_standard_hours?: Integer
  fixed_interval_payment_amount?: DecimalString
  fixed_interval_num_of_payments?: Integer
}

export enum PaymentStatus {
  INITIAL = 0,
  APPROVED = 1,
  INVOICED = 2,
  REJECTED = 3
}

export const PAYMENT_STATUS_DISPLAY_MAP = {
  [PaymentStatus.INITIAL]: 'common.created',
  [PaymentStatus.APPROVED]: 'common.ui.approved',
  [PaymentStatus.INVOICED]: 'term.invoiced',
  [PaymentStatus.REJECTED]: 'common.ui.rejected'
}

export interface Payment {
  amount: Money
  date: DateString
  status?: PaymentStatus
}

export interface OfferContractorData {
  contractor_generic_fields?: GenericFields
}

// The response from apply api is a bit of a mess
// Its sometimes either status and message or success_list and error_list, refer to job_application_apply_candidates
export interface ApplyJobApplicationResponse {
  success_list?: JobApplicationElementResponse[]
  error_list?: JobApplicationElementResponse[]
  status?: string
  message?: string
}

export interface JobApplicationElementResponse {
  cand_name?: string
  message?: string
}

export interface JobApplicationOptions {
  isPreIdentified?: boolean
  candidates: ApplicableCandidate[]
}

// when IS_PAY_RATE_PROGRAM_ENABLED is live this type shouldn't be needed and can just use JobReapplyRequest
// refactor so that isPayRateDriven is not in payload for mutation
export interface JobReapplyOptions {
  candidate_pay_rate?: string
  vendor_bill_rate?: string
  markup?: string
  start_date?: Date | null
  end_date?: Date | null
  location?: number | null
  isPayRateDriven: boolean
  calculation_pack?: number | null
}

export interface JobReapplyRequest {
  pay_rate?: string
  bill_rate?: string
  markup?: string
  start_date?: DateString
  end_date?: DateString
  location?: number
  calculation_pack_id?: number
}

export interface RejectWithdrawJobApplicationParams {
  job_app_id: number
  cds_reason?: number
  reason?: string
}

export interface BulkRejectJobApplicationParams {
  job: number
  job_app_ids: number[]
  cds_reason?: number
  reason?: string
}

export enum VendorAddressType {
  HEADQUARTER = '1',
  MAILING = '2',
  REMIT_TO = '3'
}

export interface BaseJobAppScheduleMeetingParams {
  title: string
  date: Date | string
  startTime: string
  endTime: string
  type: MeetingType
  message: string
  shareWith: string
  address?: string
  phoneNumber?: string
  videoConfDetails?: string
}

export interface JobAppScheduleMeetingParams extends BaseJobAppScheduleMeetingParams {
  bridgePin?: string
  confRoom?: string
  from: string
  to: string
  status: InterviewEventStatus
  reasonCancelInterview?: string
  jobAppId: number
}

export interface JobAppRescheduleMeetingParams extends BaseJobAppScheduleMeetingParams {
  bridgePin?: string
  confRoom?: string
  from: string
  to: string
  status: InterviewEventStatus
  reasonCancelInterview?: string
  eventId: number
}

export interface JobAppRequestRescheduleMeetingParams extends JobAppRescheduleMeetingParams {
  msg: string | Date
}

export interface JobAppCancelMeetingParams {
  decline_reason?: string
  status?: InterviewEventStatus
  eventId: number
}

export interface JobApplicationApplyError {
  body: { message: string }
}

export const useReapplyCandidateMutation = (
  workOrderId: number | undefined,
  options?: UseMutationOptions<ApplyJobApplicationResponse, JobApplicationApplyError, JobReapplyOptions>
) => {
  return useMutation({
    mutationFn: async ({
      candidate_pay_rate,
      vendor_bill_rate,
      markup,
      start_date,
      end_date,
      location,
      isPayRateDriven,
      calculation_pack
    }: JobReapplyOptions) => {
      if (workOrderId === undefined) throw new Error('Must have a Work Order Id to resubmit candidate')
      const data = {
        start_date: start_date ? formatDataDate(start_date) : undefined,
        end_date: end_date ? formatDataDate(end_date) : undefined,
        location,
        calculation_pack
      } as JobReapplyRequest
      if (isPayRateDriven) {
        data.pay_rate = candidate_pay_rate
        data.markup = floatToPercent(markup ?? 0).toFixed(12)
      } else {
        data.bill_rate = vendor_bill_rate
        data.pay_rate = candidate_pay_rate
      }

      return workOrderApi.reapplyWorkOrder(workOrderId!, data)
    },
    ...options,
    onSuccess(...args) {
      if (options?.onSuccess !== undefined) options.onSuccess(...args)
      queryClient.invalidateQueries([JOB_QUERY])
      invalidateJobApplicationQueries()
    }
  })
}
export const useScheduleMeetingJobAppMutation = (
  opts?: UseMutationOptions<JobAppScheduleMeetingParams, unknown, any>
) => {
  return useMutation({
    mutationFn: async (data: JobAppScheduleMeetingParams) => {
      return jobApplicationsApi.scheduleMeeting(data)
    },
    ...opts,
    onSuccess(...args) {
      opts?.onSuccess?.(...args)
      invalidateJobApplicationQueries()
    }
  })
}

export const useRescheduleMeetingJobAppMutation = (
  opts?: UseMutationOptions<JobAppRescheduleMeetingParams, unknown, any>
) => {
  return useMutation({
    mutationFn: async (data: JobAppRescheduleMeetingParams) => {
      return jobApplicationsApi.rescheduleMeeting(data)
    },
    ...opts,
    onSuccess(...args) {
      if (opts?.onSuccess !== undefined) {
        opts.onSuccess(...args)
      }
      queryClient.invalidateQueries(interviewKeys.all)
    }
  })
}

export const useRequestRescheduleJobAppMutation = (
  opts?: UseMutationOptions<void, unknown, JobAppRequestRescheduleMeetingParams>
) => {
  return useMutation({
    mutationFn: async (data: JobAppRequestRescheduleMeetingParams) => {
      return jobApplicationsApi.requestRescheduleMeeting(data)
    },
    ...opts,
    onSuccess(...args) {
      if (opts?.onSuccess !== undefined) {
        opts.onSuccess(...args)
      }
      queryClient.invalidateQueries(interviewKeys.all)
    }
  })
}

export const useCancelMeetingJobAppMutation = (opts?: UseMutationOptions<JobAppCancelMeetingParams, unknown, any>) => {
  return useMutation({
    mutationFn: async (data: JobAppCancelMeetingParams) => {
      return jobApplicationsApi.cancelMeeting(data)
    },
    ...opts,
    onSuccess(...args) {
      if (opts?.onSuccess !== undefined) {
        opts.onSuccess(...args)
      }
      queryClient.invalidateQueries(interviewKeys.all)
    }
  })
}

export const useRejectJobAppMutation = (opts?: UseMutationOptions<void, unknown, any>) => {
  return useMutation({
    mutationFn: async (data: RejectWithdrawJobApplicationParams) => {
      return jobApplicationsApi.reject(data)
    },
    ...opts,
    onSuccess(...args) {
      if (opts?.onSuccess !== undefined) {
        opts.onSuccess(...args)
      }
      queryClient.invalidateQueries([JOB_QUERY])
      invalidateJobApplicationQueries()
    }
  })
}

export const useBulkRejectJobAppMutation = (opts?: UseMutationOptions<void, unknown, any>) => {
  return useMutation({
    mutationFn: async (data: BulkRejectJobApplicationParams) => {
      return jobApplicationsApi.bulk_reject(data)
    },
    ...opts,
    onSuccess(...args) {
      if (opts?.onSuccess !== undefined) {
        opts.onSuccess(...args)
      }
      queryClient.invalidateQueries([JOB_QUERY])
      invalidateJobApplicationQueries()
    }
  })
}

export const useWithdrawJobAppMutation = (opts?: UseMutationOptions<void, unknown, any>) => {
  return useMutation({
    mutationFn: async (data: RejectWithdrawJobApplicationParams) => {
      return jobApplicationsApi.withdraw(data)
    },
    ...opts,
    onSuccess(...args) {
      if (opts?.onSuccess !== undefined) {
        opts.onSuccess(...args)
      }
      queryClient.invalidateQueries([JOB_QUERY])
      invalidateJobApplicationQueries()
    }
  })
}

export type ReadyToOnboardJobApplicationListItem = {
  current_status: JobApplicationStatus
  id: PrimaryKey
  work_order_id: PrimaryKey
}

export type ReadyToRejectJobApplicationListItem = {
  current_status: JobApplicationStatus
  id: PrimaryKey
}

export type JobApplicationBulkActionsInfo = {
  ready_to_onboard_count?: number
  ready_to_onboard_job_apps?: ReadyToOnboardJobApplicationListItem[]
  can_be_rejected_count: number
  can_be_rejected_job_apps: ReadyToRejectJobApplicationListItem[]
}

export type JobApplicationPaginationResponse<T> = OffsetLimitPaginationResponse<T> & {
  bulk_actions_info?: JobApplicationBulkActionsInfo
}

export type JobApplicationResponse = JobApplicationPaginationResponse<JobApplicationListItem>

export type JobApplicationListParams = {
  search?: string
  status?: VendorStatus
  limit?: number
  offset?: number
  job_id?: number // job_id is required if you only want the applicants for a specific job
  candidate_source?: string
}

export const useJobApplicationsQuery = (
  queryParams: JobApplicationListParams,
  queryOptions: UseQueryOptions<JobApplicationResponse>
) => {
  const key = jobApplicationKeys.list(queryParams)
  return useQuery({
    queryKey: key,
    queryFn: () => jobApplicationsApi.listJobApplications(queryParams),
    ...queryOptions,
    keepPreviousData: true
  })
}

export const invalidateJobApplicationsQuery = (queryParams: JobApplicationListParams) => {
  return queryClient.invalidateQueries(jobApplicationKeys.list(queryParams))
}

export const useJobApplicationConfigQuery = (queryParams: Record<string, any> = {}) => {
  const key = jobApplicationKeys.config(queryParams)
  return useQuery({
    queryKey: key,
    queryFn: () => jobApplicationsApi.config(queryParams),
    staleTime: 1000 * 60 * 5
  })
}

export const invalidateJobApplicationConfigQuery = async () =>
  queryClient.invalidateQueries(jobApplicationKeys.config())

export type Office365MeetingSuggestions = {
  end_datetime: string
  end_time: string
  start_datetime: string
  start_time: string
}

export const useOffice365MeetingSuggestionsQuery = (
  date?: string,
  timezone?: string,
  config?: UseQueryOptions<Office365MeetingSuggestions[]>
) => {
  const key = office365MeetingSuggestionKeys.list({ date, timezone })
  return useQuery({
    queryKey: key,
    queryFn: () => jobApplicationsApi.office365MeetingSuggestions(date!, timezone),
    enabled: !!date && !(config?.enabled === false),
    staleTime: 1000 * 60 * 5,
    ...config
  })
}

export type JobFormConfigForOffer = {
  timesheet_approvers: {
    required: boolean
    multiselect: boolean
    optionSubLabel: string | null
  }
  expense_approvers: {
    required: boolean
    multiselect: boolean
    optionSubLabel: string | null
  }
  resource_manager: {
    optionSubLabel: string | null
  }
  hiring_team: {
    optionSubLabel: string | null
  }
}
export type OfferConfigAuthenticationMethod = {
  show_on_onboarding: boolean
  can_edit: boolean
  auth_providers: { value: number; label: string }[]
}
export type OfferConfig = {
  job_max_duration?: number
  can_override_max_duration_of_job: boolean
  can_override_work_order_max_duration: boolean
  is_budget_calculator_enabled: boolean
  rate_config: {
    [key in (typeof PayType)[keyof typeof PayType]]: { min_rate: number; max_rate?: number }
  }
  is_classify_clocks_enabled: boolean
  allow_wo_dates_to_be_outside_job_dates: boolean
  specify_wo_end_time: boolean
  is_workday_position_selection_applicable: boolean
  job_form_config: JobFormConfigForOffer
  is_independent_contractor: boolean
  should_recalculate_labor_amount: boolean
  is_labor_amount_on_budget_overridden: boolean
  has_permission_to_edit_labor_amount_override_on_budget: boolean
  apply_shifts: boolean
  can_override_shifts: boolean
  are_shifts_required: boolean
  event_based_expenses: {
    can_view: boolean
    can_edit: boolean
    required: boolean
  }
  job_module: ModuleType
  work_type_information: {
    can_update_work_type_profile: boolean
    is_work_type_profile_read_only?: boolean
    can_add_work_type_overrides: boolean
  }
  work_order_max_duration: number | null
  can_view_candidate_pay_rate: boolean
  can_update_candidate_pay_rate: boolean
  can_edit_rate_schedule: boolean
  run_overtime_profile_rule?: boolean
  can_vendor_onboard_sow_job?: boolean
  can_update_tax_override: boolean
  should_show_additional_markups: boolean
  show_rate_schedule: boolean
  guided_rate_schedule_applicable: boolean
  is_fast_path: boolean
  is_preidentified: boolean
  is_location_locked_by_formula_group: boolean
  is_worker_classification_locked_by_formula_group: boolean
  is_source_type_locked_by_formula_group: boolean
  authentication_method?: OfferConfigAuthenticationMethod
}

interface OfferConfigParams {
  jobAppId?: number
  jobId?: number
  is_onboarding?: boolean
}

const offerConfigQuery = (params: OfferConfigParams) =>
  queryOptions({
    queryKey: offerReleaseConfigKeys.detail(params),
    queryFn: () => jobApplicationsApi.offerConfig(params),
    staleTime: 1000 * 5,
    enabled: !!params?.jobAppId || !!params?.jobId
  })

export const useOfferConfigQuery = (params: Pick<OfferConfigParams, 'jobAppId'>) => {
  return useQuery(offerConfigQuery(params))
}

export const useOnboardConfigQuery = (params: Pick<OfferConfigParams, 'jobId'>) => {
  return useQuery(offerConfigQuery({ ...params, is_onboarding: true }))
}

export type ToggleApplicantShortlist = { id: number; data: { is_shortlisted: boolean } }
export const useToggleApplicantShortlistMutation = (
  opts?: UseMutationOptions<void, unknown, ToggleApplicantShortlist>
) => {
  return useMutation({
    mutationFn: ({ id, data }) => fetch.patch(`/api/v2/job_applications/${id}/shortlist/`, data),
    ...opts,
    onSuccess: async (...args) => {
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
      invalidateJobApplicationQueries()
    },
    onError: (...args) => {
      if (typeof opts?.onError === 'function') opts.onError(...args)
    }
  })
}

export type ContractorBillRateResponse = {
  bill_rate?: number
  override_message?: string
}

type ContractorBillRateParams = {
  fee_profile_id?: number
  work_site_id: number
}

export const useContractorBillRateQuery = (
  id: number,
  queryParams: ContractorBillRateParams,
  queryOptions?: UseQueryOptions<ContractorBillRateResponse>
) => {
  return useQuery({
    queryKey: ['contractor_bill_rate', queryParams],
    queryFn: () => jobApplicationsApi.getBillRate(id, queryParams),
    ...queryOptions
  })
}

export const useAcceptOfferMutation = () => {
  return useMutation({
    mutationFn: (jobAppId: number) => jobApplicationsApi.acceptOffer(jobAppId),
    onSuccess() {
      invalidateJobApplicationQueries()
    }
  })
}
const jobApplicationOfferFormQuery = (id?: number, opts?: UseQueryOptions<JobOfferFormData>) =>
  queryOptions({
    queryKey: jobApplicationKeys.detail(id),
    queryFn: () => jobApplicationsApi.offerForm(id!),
    staleTime: 1000 * 5,
    enabled: !!id,
    ...opts
  })

export const useJobApplicationOfferFormQuery = (id?: number, queryOptions?: UseQueryOptions<JobOfferFormData>) => {
  return useQuery(jobApplicationOfferFormQuery(id, queryOptions))
}

export type ApplicantDetails = {
  id: number
  full_name: string | null
  first_name: string | null
  middle_name: string | null
  last_name: string | null
  preferred_first_name: string | null
  preferred_last_name: string | null
  experience: number | null
  email: string | null
  alternate_email: string | null
  formatted_contact_number: string | null
  formatted_alternate_contact_number: string | null
  address: Partial<AddressData> | string | null
  country_name: string | null
  residence_status_display: string | null
}

export const useApplicantDetailsQuery = (jobApplicationId?: number, queryOptions?: UseQueryOptions<ApplicantDetails>) =>
  useQuery({
    queryKey: [...jobApplicationKeys.detail(jobApplicationId), 'applicant-details'],
    queryFn: () => jobApplicationsApi.applicantDetails(jobApplicationId!),
    enabled: Number.isInteger(jobApplicationId),
    ...queryOptions
  })

export const useUndoRejectionMutation = (opts?: UseMutationOptions<void, unknown, { workOrderId: number }>) => {
  return useMutation({
    mutationFn: async ({ workOrderId }: { workOrderId: number }) => jobApplicationsApi.undoReject(workOrderId),
    ...opts,
    onSuccess(...args) {
      if (opts?.onSuccess !== undefined) {
        opts.onSuccess(...args)
      }
      queryClient.invalidateQueries([JOB_QUERY])
      invalidateJobApplicationQueries()
    },
    onError(...args) {
      if (opts?.onError !== undefined) {
        opts.onError(...args)
      }
    }
  })
}

// TODO (vic) - clean up now that WOAA exists
export const useWorkTypesByWorkTypeProfileQuery = (job_app_id: number, work_type_profile_id: number) =>
  useQuery({
    queryKey: [JOB_APPLICATION, job_app_id, WORK_TYPES_QUERY_KEY, work_type_profile_id],
    queryFn: () => jobApplicationsApi.workTypes(job_app_id, work_type_profile_id),
    enabled: !!job_app_id
  })
